import { logEvent, setUserId, setUserProperties, type Analytics } from 'firebase/analytics';
import { getAnalyticsInstance, runWhenAnalyticsReady } from './firebase';

// v1 client taxonomy. Server-owned events (sign_up, game_session_start/end,
// purchase) are emitted from the backend via Measurement Protocol and are NOT
// fired here, keeping each event single-sourced prevents double-counting.
export type AnalyticsEvent =
  | 'page_view'
  | 'login'
  | 'logout'
  | 'waitlist_join'
  // Acquisition: the paid ad click landed on /signup (top of the campaign funnel).
  // Fired once per page mount; mirrored to the Meta pixel for ad attribution.
  | 'signup_page_view'
  | 'character_create_start'
  | 'character_create_complete'
  // Activation: player reached the lobby (= started playing). Fired once per
  // browser. The paid-campaign funnel's deepest goal: click → signup → lobby.
  | 'reached_lobby'
  | 'npc_chat_open'
  | 'app_error'
  // PM token purchase intent (client, loss-tolerant). GA4 reserved ecommerce
  // view_item / begin_checkout power native ecommerce funnels; `purchase`
  // (revenue) is server-owned. pm_purchase_* customs cover cancel/timeout/error.
  | 'view_item'
  | 'begin_checkout'
  | 'pm_purchase_canceled'
  | 'pm_purchase_processing'
  | 'pm_purchase_error'
  // Token-wall → Discord feedback funnel: clicked the "join Discord & give
  // feedback for bonus tokens" CTA. Params: { surface, low_balance }.
  | 'discord_feedback_cta_click'
  // Token-wall → invite-friends funnel: clicked "Invite friends" (opens the
  // account Invite tab, the highest-yield earn path). Params: { surface, low_balance }.
  | 'invite_cta_click'
  // Token-wall → "more ways to earn" (opens the account Profile tab: buy more +
  // Discord/X rewards). Params: { surface, low_balance }.
  | 'earn_more_cta_click'
  // Dashboard cliffhanger resume card (FTUE retention loop). Params:
  // { state: 'live'|'ended_incomplete', encounter_type? }.
  | 'resume_card_shown'
  | 'resume_card_click'
  // Growth promo-banner (lobby + in-game, one at a time: invite → Discord).
  // `banner_impression` fires once per variant per mount; `banner_cta_click` on
  // a CTA tap. Param: { banner: 'invite' | 'discord' }. Register `banner` as a
  // GA4 custom dimension (analytics-overhaul.md convention) so it's queryable.
  | 'banner_impression'
  | 'banner_cta_click'
  // Multiplayer promo: the empty Friends panel shows a "play with friends" card
  // linking to the /multiplayer guide. `_click` on the CTA tap. Param:
  // { surface: 'friends_panel_empty' }.
  | 'multiplayer_promo_click';

type AnalyticsParams = Record<string, unknown>;

// Firebase's logEvent overloads enforce per-reserved-event param shapes; we use a uniform
// permissive signature here and trust callers to send sensible params per event.
const logEventLoose = logEvent as (
  analytics: Analytics,
  eventName: string,
  eventParams?: AnalyticsParams
) => void;

export function track(event: AnalyticsEvent, params?: AnalyticsParams): void {
  runWhenAnalyticsReady(() => {
    const analytics = getAnalyticsInstance();
    if (!analytics) return;
    logEventLoose(analytics, event, params);
  });
}

export function identifyUser(userId: string, properties?: AnalyticsParams): void {
  runWhenAnalyticsReady(() => {
    const analytics = getAnalyticsInstance();
    if (!analytics) return;
    setUserId(analytics, userId);
    if (properties) {
      setUserProperties(analytics, properties);
    }
  });
}

export function clearUser(): void {
  runWhenAnalyticsReady(() => {
    const analytics = getAnalyticsInstance();
    if (!analytics) return;
    setUserId(analytics, null);
  });
}

// X (Twitter) Ads conversions. GA4 (above) is the master dashboard; this only
// feeds X Ads Manager so it can attribute signups to ad spend. Logical event
// names map to the real X event IDs handed back from X Ads Manager via env vars
// — both no-op when unset (dev/staging), exactly like the Firebase analytics
// init. The twq queue stub (x-pixel.ts) buffers calls until uwt.js loads, so no
// extra deferral is needed here.
// `guest_start` is X-only: it has no GA4 twin here because the backend already
// emits the master `sign_up(method="anonymous", invite_source="guest_start")`
// server-side on provisioning (single-sourced). X needs its own pixel event
// because `signup_complete` fires only on guest→full CONVERT, so without this a
// paid click that picks a nickname and plays never registers as an X conversion.
// `signup_page_view` is the paid click-through landing on /signup (the top of the
// campaign funnel). Like the others it needs its own X event ID created in X Ads
// Manager; until `VITE_X_PAGEVIEW_EVENT_ID` is set it no-ops, so shipping this
// ahead of the event's creation is safe (same pattern guest_start followed).
type XConversionEvent = 'signup_complete' | 'reached_lobby' | 'guest_start' | 'signup_page_view';

const X_EVENT_IDS: Record<XConversionEvent, string> = {
  signup_complete: import.meta.env.VITE_X_SIGNUP_EVENT_ID || '',
  reached_lobby: import.meta.env.VITE_X_LOBBY_EVENT_ID || '',
  guest_start: import.meta.env.VITE_X_GUEST_EVENT_ID || '',
  signup_page_view: import.meta.env.VITE_X_PAGEVIEW_EVENT_ID || '',
};

export function fireXConversion(event: XConversionEvent, params?: AnalyticsParams): void {
  const eventId = X_EVENT_IDS[event];
  if (!eventId || typeof window === 'undefined' || typeof window.twq !== 'function') return;
  window.twq('event', eventId, params ?? {});
}

// Activation conversion: the player reached the lobby (= started playing), the
// deepest goal of the paid funnel (click → signup → lobby). Fired at most once
// per browser so repeat lobby visits don't inflate the count. Mirrors to GA4
// (master dashboard) and the X pixel (ad attribution). No user identifier is
// passed to X — it attributes via its own pixel cookie, and sending our stable
// Firebase UID to a third-party ad network would be an unnecessary leak. GA4
// identity is set separately via identifyUser().
const REACHED_LOBBY_KEY = 'pm_reached_lobby_fired';

export function trackReachedLobbyOnce(): void {
  try {
    if (localStorage.getItem(REACHED_LOBBY_KEY)) return;
    localStorage.setItem(REACHED_LOBBY_KEY, '1');
  } catch {
    // localStorage blocked (private mode / disabled). Fall through and fire;
    // at worst the event repeats, which is preferable to never recording it.
  }
  track('reached_lobby');
  fireXConversion('reached_lobby');
  fireMetaConversion('reached_lobby');
}

// Guest-signup conversion (X-only; GA4 covers it server-side via `sign_up`). Keyed
// on the anonymous uid rather than a global flag like reached_lobby: a genuinely
// new guest after sign-out is a distinct uid + a distinct backend provision + gift
// and SHOULD attribute, but a transient post-provision failure (e.g. the
// displayName-mirror token refresh) that makes the user retry the SAME uid must
// not double-count an already-provisioned guest. So fire at most once per uid.
const GUEST_START_FIRED_KEY = 'pm_guest_start_fired_uid';

export function fireGuestStartOnce(uid: string): void {
  try {
    if (localStorage.getItem(GUEST_START_FIRED_KEY) === uid) return;
    localStorage.setItem(GUEST_START_FIRED_KEY, uid);
  } catch {
    // localStorage blocked (private mode / disabled). Fall through and fire; at
    // worst the event repeats on a retry, preferable to never attributing it.
  }
  fireXConversion('guest_start');
  // Meta's primary paid conversion (picked a nickname → real player). Deduped
  // against the server-side CAPI CompleteRegistration by a shared per-uid eventID.
  fireMetaConversion('guest_start', undefined, metaRegistrationEventId(uid));
}

// Meta (Facebook/Instagram) Ads conversions. GA4 (above) is the master dashboard;
// this feeds Meta Ads Manager for attribution + delivery optimization. The two
// events the paid Meta campaign is bought on: `signup_page_view` (ad click →
// /signup landing, the click-through) and `guest_start` (picked a nickname → real
// player, the full conversion). Unlike X, Meta needs only the one pixel ID
// (VITE_META_PIXEL_ID) — event names are standard/custom strings, not per-event
// IDs. No user identifier is sent to the browser pixel (Meta matches via its own
// fbp cookie + IP/UA); the server-side CAPI adds a hashed external_id.
type MetaConversionEvent = 'signup_page_view' | 'guest_start' | 'reached_lobby' | 'signup_complete';

// Logical event → Meta event name + whether it's a Meta *standard* event (fbq
// 'track') or a *custom* one (fbq 'trackCustom'). guest_start maps to the standard
// CompleteRegistration so Meta can optimize delivery toward it.
const META_EVENTS: Record<MetaConversionEvent, { name: string; standard: boolean }> = {
  signup_page_view: { name: 'ViewContent', standard: true },
  guest_start: { name: 'CompleteRegistration', standard: true },
  reached_lobby: { name: 'ReachedLobby', standard: false },
  signup_complete: { name: 'AccountUpgrade', standard: false },
};

// FNV-1a 64-bit lowercase-hex of the uid (mirrors the GA4 client_id derivation in
// ga4.rs). A stable, non-reversible token. MUST produce the byte-identical string
// to meta_capi.rs::fnv1a_hex. Firebase uids are ASCII, so per-char codes == bytes.
function fnv1aHex(input: string): string {
  const OFFSET = 0xcbf29ce484222325n;
  const PRIME = 0x100000001b3n;
  const MASK = 0xffffffffffffffffn;
  let hash = OFFSET;
  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i) & 0xff);
    hash = (hash * PRIME) & MASK;
  }
  return hash.toString(16).padStart(16, '0');
}

// Per-uid event_id so the browser CompleteRegistration and the server-side CAPI
// event for the same guest dedup into ONE (Meta keys dedup on event_name +
// event_id). Derived from a NON-REVERSIBLE hash of the uid: the event_id is
// transmitted to Meta by BOTH the pixel and CAPI, so the raw Firebase uid must
// never appear in it. MUST match meta_capi.rs::registration_event_id.
export function metaRegistrationEventId(uid: string): string {
  return `CompleteRegistration:${fnv1aHex(uid)}`;
}

export function fireMetaConversion(
  event: MetaConversionEvent,
  params?: AnalyticsParams,
  eventId?: string
): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  const { name, standard } = META_EVENTS[event];
  const method = standard ? 'track' : 'trackCustom';
  if (eventId) window.fbq(method, name, params ?? {}, { eventID: eventId });
  else window.fbq(method, name, params ?? {});
}

// Signup-page visit: the paid click-through landing on /signup, the top of the
// campaign funnel and one of the two metrics the paid campaigns are bought on
// (the other is guest_start). Fired once per mount (each visit is a distinct
// landing we want to count) to GA4 (master dashboard) + BOTH paid pixels (X and
// Meta) for attribution + delivery optimization on each channel.
export function trackSignupPageView(): void {
  track('signup_page_view');
  fireXConversion('signup_page_view');
  fireMetaConversion('signup_page_view', { content_name: 'signup' });
}
